import React, { FC, ReactNode, useMemo } from 'react';
import { alea } from 'seedrandom';
import { tintSafe } from '../../../CodesThatsGottaGoSomewhereElse';
import { toRadians } from '../../../platform/geom/Math';
import { Point } from '../../../platform/geom/Point';
import { RenderComments, RenderTags, RenderVotes } from '../../canvas/visualization/RenderAnnotations';
import {
    RenderBounds,
    RenderBox,
    RenderConcealed,
    RenderConcealedEffect,
    RenderCounter, RenderEmoticon, RenderGraffiti,
    RenderHeadline,
    RenderInner,
    RenderUrl,
    RenderUserInfo,
    useBracketShortcut,
    usePose,
    usePoseEffect,
    useUser
} from '../../canvas/visualization/RenderHelpers';
import { RenderRankingMedal } from '../../canvas/visualization/RenderRankingMedal';
import { RenderText } from '../../canvas/visualization/RenderText';
import { RenderTask } from '../assignment/RenderTask';
import { IsConcealedHintVisible } from '../conceal/Concealable';
import { VoteRankAttr } from '../voting/Vote';
import { Sticky, } from './Sticky';

const usePoseDerivatives = (object: Sticky) => {
    const p = usePose(object);
    return useMemo(() => ({
        highlighted: !!(p.pressed || p.targeted || p.selected) && !p.beingManipulated,
        selected: p.selected,
        editing: p.beingEdited,
    }), [p]);
};

const useTilt = (object: Sticky) => {
    const { taskId } = object;
    const { highlighted } = usePoseDerivatives(object);
    const rotation = useMemo(() => {
        const r = alea(object.id, { global: true })();
        return toRadians(Math.floor(r * 5) - 2);
    }, [object.id]);
    return !!taskId || highlighted ? 0 : rotation;
};

const RenderTilt = React.memo<{ tilt: number, children: ReactNode }>(({ tilt, children }) => {
    return <RenderBox inset="0" transform={`rotate(${tilt || 0}rad)`} transition="transform 100ms linear">
        {children}
    </RenderBox>;
});

const RenderDesign = React.memo<{ color: string, tilt: number, highlighted: boolean, selected: boolean }>(({ color, tilt, highlighted, selected }) => {
    if (!color) return null;

    const pt = new Point(0, 3).rotate(tilt * -1);
    const shadow = `${pt.x}px ${pt.y}px 10px 0 rgba(0, 0, 0, 0.1)`;

    const fill = tintSafe(.75, color);
    const border = tintSafe(selected ? 0 : .5, color);

    return <RenderBox
        inset="0"
        background={fill}
        borderRadius="2px"
        border={highlighted ? `solid 2px ${border}` : null}
        boxShadow={shadow}
    />;
});

export const StickyVisual: FC<{ object: Sticky, children?: ReactNode }> = ({ object, children }) => {

    const { highlighted, selected, editing } = usePoseDerivatives(object);
    const color = object.autoColor || object.background;
    const tilt = useTilt(object);
    const user = useUser(object);

    const bs = useBracketShortcut(object.content);
    const showUser = !editing && bs.type === 'none' && highlighted && !!user && !IsConcealedHintVisible.get(object) && !object.taskId;
    const showHint = !editing && bs.type === 'none' && IsConcealedHintVisible.get(object);
    const flipEffect = usePoseEffect(object, 'flip');

    return <>
        <RenderBounds object={object}>
            <RenderInner object={object}>
                <RenderTilt tilt={tilt}>
                    <RenderDesign {...{ color, tilt, highlighted, selected }} />
                    <RenderGraffiti object={object} />
                    {!editing && <>
                        {bs.type === 'none' && <RenderText {...object.layout.content} value={object.content} formatOpts={['mention', 'bold', 'italic']} />}
                        {bs.type === 'emoticon' && <RenderEmoticon value={bs.value} size={Math.min(object.width, object.height) * .7} />}
                        {bs.type === 'user' && <RenderUserInfo value={bs.value} />}
                        {bs.type === 'url' && <RenderUrl value={bs.value} />}
                    </>}
                    <RenderComments object={object} />
                    <RenderVotes object={object} />
                    <RenderTags object={object} />
                    <RenderRankingMedal value={VoteRankAttr.get(object)} show={!highlighted} />
                    {showUser &&
                        <RenderHeadline>
                            <RenderCounter user={user} />
                        </RenderHeadline>
                    }
                    {showHint &&
                        <RenderHeadline>
                            <RenderConcealed object={object} />
                        </RenderHeadline>
                    }
                    <RenderConcealedEffect effect={flipEffect} />
                    <RenderTask object={object} />
                </RenderTilt>
            </RenderInner>
        </RenderBounds>
        {children}
    </>;
};
